import { 
  collection, 
  query, 
  where, 
  getDocs,
  orderBy,
  limit as firestoreLimit
} from 'firebase/firestore';
import { db } from './config';
import { ProgramBeneficiary } from './programFirestoreService';
import { Timestamp } from 'firebase/firestore';

export interface SearchResult {
  beneficiary: ProgramBeneficiary;
  programTitle: string;
  registrationDate: string;
}

/**
 * Search beneficiaries across all programs by Ghana Card number or name
 */
export const searchBeneficiaryByGhanaCard = async (searchTerm: string): Promise<SearchResult[]> => {
  try {
    const results: SearchResult[] = [];
    
    // Get all programs
    const programsRef = collection(db, 'programs');
    const programsSnapshot = await getDocs(query(programsRef, where('isDeleted', '==', 0), firestoreLimit(100)));
    
    // Search in each program's registrations subcollection
    for (const programDoc of programsSnapshot.docs) {
      const programId = programDoc.id;
      const programData = programDoc.data();
      const programTitle = programData.title || 'Unknown Program';
      
      const registrationsRef = collection(db, 'programs', programId, 'registrations');

      // First, search by exact Ghana Card number (case-insensitive by uppercasing)
      try {
        const idSnapshot = await getDocs(query(registrationsRef, where('idNumber', '==', searchTerm.toUpperCase()), firestoreLimit(5)));
        idSnapshot.docs.forEach(doc => {
          const data = doc.data() as ProgramBeneficiary;
          const existing = results.find(r => r.beneficiary.beneficiaryId === data.beneficiaryId || (data.idNumber && r.beneficiary.idNumber === data.idNumber));
          if (!existing) {
            results.push({
              beneficiary: { ...data, id: doc.id },
              programTitle,
              registrationDate: data.registrationDate instanceof Timestamp ? data.registrationDate.toDate().toLocaleDateString() : 'Unknown date'
            });
          }
        });
      } catch (err) {
        console.error('Error searching by idNumber:', err);
      }

      // Then perform a case-insensitive partial name match by fetching registrations and filtering client-side
      try {
        const allSnap = await getDocs(query(registrationsRef, orderBy('registrationDate', 'desc'), firestoreLimit(50)));
        const lowerTerm = searchTerm.toLowerCase();
        allSnap.docs.forEach(doc => {
          const data = doc.data() as ProgramBeneficiary;
          const fullName = (data.fullName || '').toString().toLowerCase();
          if (fullName.includes(lowerTerm)) {
            const existing = results.find(r => r.beneficiary.beneficiaryId === data.beneficiaryId || (data.idNumber && r.beneficiary.idNumber === data.idNumber));
            if (!existing) {
              results.push({
                beneficiary: { ...data, id: doc.id },
                programTitle,
                registrationDate: data.registrationDate instanceof Timestamp ? data.registrationDate.toDate().toLocaleDateString() : 'Unknown date'
              });
            }
          }
        });
      } catch (err) {
        console.error('Error searching by name client-side:', err);
      }
    }
    
    // Limit results to 10
    return results.slice(0, 10);
  } catch (error) {
    console.error('Error searching beneficiaries:', error);
    return [];
  }
};

/**
 * Get beneficiary registration history across all programs
 */
export const getBeneficiaryHistory = async (beneficiaryId: string): Promise<SearchResult[]> => {
  try {
    const results: SearchResult[] = [];
    
    const programsRef = collection(db, 'programs');
    const programsSnapshot = await getDocs(query(programsRef, where('isDeleted', '==', 0), firestoreLimit(100)));
    
    for (const programDoc of programsSnapshot.docs) {
      const programId = programDoc.id;
      const programData = programDoc.data();
      const programTitle = programData.title || 'Unknown Program';
      
      const registrationsRef = collection(db, 'programs', programId, 'registrations');
      const q = query(registrationsRef, where('beneficiaryId', '==', beneficiaryId), firestoreLimit(10));
      const snapshot = await getDocs(q);
      
      snapshot.docs.forEach(doc => {
        const data = doc.data() as ProgramBeneficiary;
        results.push({
          beneficiary: {
            ...data,
            id: doc.id
          },
          programTitle,
          registrationDate: data.registrationDate instanceof Timestamp 
            ? data.registrationDate.toDate().toLocaleDateString() 
            : 'Unknown date'
        });
      });
    }
    
    // Sort by registration date (newest first)
    return results.sort((a, b) => {
      const dateA = a.beneficiary.registrationDate instanceof Timestamp 
        ? a.beneficiary.registrationDate.toDate() 
        : new Date(0);
      const dateB = b.beneficiary.registrationDate instanceof Timestamp 
        ? b.beneficiary.registrationDate.toDate() 
        : new Date(0);
      return dateB.getTime() - dateA.getTime();
    });
  } catch (error) {
    console.error('Error getting beneficiary history:', error);
    return [];
  }
};

/**
 * Check if beneficiary exists in any program
 */
export const beneficiaryExists = async (idNumber: string): Promise<boolean> => {
  try {
    const programsRef = collection(db, 'programs');
    const programsSnapshot = await getDocs(query(programsRef, where('isDeleted', '==', 0), firestoreLimit(100)));
    
    for (const programDoc of programsSnapshot.docs) {
      const programId = programDoc.id;
      const registrationsRef = collection(db, 'programs', programId, 'registrations');
      const q = query(registrationsRef, where('idNumber', '==', idNumber.toUpperCase()), firestoreLimit(1));
      const snapshot = await getDocs(q);
      
      if (!snapshot.empty) {
        return true;
      }
    }
    
    return false;
  } catch (error) {
    console.error('Error checking if beneficiary exists:', error);
    return false;
  }
};
